import {
  Button,
  Col,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
} from '@ant-design/icons';
import { ICompanyView } from '../../../../domain/interfaces/ICompanyView';
import { degradedModeAsString, EDegradedMode } from '@leedpulse-monorepo/enums';
import { advancedOnRow } from '../../../../core/utils/commonUtils';
import { COMPANIES_PAGE_PATH } from '../../../pages/companies/CompaniesPage';
import UserPartnerLogo from '../../user_partner_logo/UserPartnerLogo';

interface CompaniesListProps {
  companies: ICompanyView[];
  loading: boolean;
  page: number;
  nbHits: number;
  hitsPerPage: number;
  onPageChange: (page: number) => void;
  onScrollToTop?: () => void;
  onRemove?: (companyId: string) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

const CompaniesList: React.FC<CompaniesListProps> = ({
  companies,
  loading,
  page,
  nbHits,
  hitsPerPage,
  onPageChange,
  onScrollToTop,
  onRemove,
  onPageSizeChange,
}) => {
  const navigate = useNavigate();

  const handlePaginationChange = (antPage: number) => {
    onPageChange(antPage - 1);
    onScrollToTop?.();
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    onPageSizeChange?.(newPageSize);
  };

  const renderRemoveButton = (record: ICompanyView) => (
    <Popconfirm
      title="Êtes-vous sûr de vouloir supprimer cette compagnie ?"
      onConfirm={(e) => {
        e?.stopPropagation();
        onRemove?.(record.objectID);
      }}
      okText="Oui"
      cancelText="Non"
    >
      <Tooltip placement="left" title="Supprimer la compagnie">
        <Button
          type={'default'}
          style={{ color: '#6737ff' }}
          icon={<DeleteOutlined style={{ color: '#FF0000' }} />}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </Tooltip>
    </Popconfirm>
  );

  const columns = [
    {
      title: 'Nom',
      render: (record: ICompanyView) => (
        <Col>
          <Row align="middle">
            <UserPartnerLogo partnerSystem={record.partner_system !== '' ?record.partner_system as string : 'leedflow'} />
            {record.name}
          </Row>
        </Col>
      ),
    },
    {
      title: 'Plan',
      key: 'plans_by_id',
      render: (record: ICompanyView) => {
        const byPlan = record.plans_by_id ?? {};
        const entries = Object.entries(byPlan);
        if (entries.length === 0) return <span>-</span>;

        return (
          <Space size={[6, 6]} wrap>
            {entries.map(([planId, { label, quantity }]) => (
              <Tag key={planId}>
                {label} · {quantity}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Statut',
      key: 'degraded_mode',
      render: (record: ICompanyView) => {
        const mode = (record.degraded_mode ?? EDegradedMode.OK) as EDegradedMode;
        const color =
          mode === EDegradedMode.UNKNOWN
            ? 'purple'
            : mode === EDegradedMode.CANCELLED
              ? 'red'
              : mode === EDegradedMode.PAUSED
                ? 'orange'
                : 'green';

        return <Tag color={color}>{degradedModeAsString(mode)}</Tag>;
      },
    },
    {
      title: '',
      key: 'access',
      render: (record: ICompanyView) => (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}
        >
          {renderRemoveButton(record)}
        </div>
      ),
    },
  ];

  return (
    <Table
      pagination={{
        current: page + 1,
        total: nbHits,
        pageSize: hitsPerPage,
        onChange: handlePaginationChange,
        onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
        showSizeChanger: true,
        simple: true,
        pageSizeOptions: [20, 50, 100, 200],
        position: ['bottomRight', 'topRight'],
        disabled: loading,
      }}
      loading={loading}
      columns={columns}
      rowKey={(record) => record.objectID}
      onRow={(record) =>
        advancedOnRow({
          url: `${COMPANIES_PAGE_PATH}/${record.objectID}`,
          navigate,
          className: 'table-row',
        })
      }
      dataSource={companies}
    />
  );
};

export default CompaniesList;
